import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ChevronRight, Mail, Linkedin, Facebook, Instagram, Building2 } from 'lucide-react';
import { internalLink } from '../lib/utils';
import EmailLink from './EmailLink';

const SITE_URL = 'https://www.fleettrackholland.nl';

const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    solutions: [
      { name: 'GPS Tracking', href: '/gps-tracking/' },
      { name: 'Voertuig Volgsysteem', href: '/gps-tracking/voertuig-volgsysteem/' },
      { name: 'GPS Tracker Bedrijfswagen', href: '/gps-tracking/bedrijfswagen/' },
      { name: 'GPS Tracker Bestelwagen', href: '/gps-tracking/bestelwagen/' },
      { name: 'GPS Tracker Vrachtwagen', href: '/gps-tracking/vrachtwagen/' },
      { name: 'Fleet Management', href: '/fleet-management/' },
      { name: 'Rittenregistratie', href: '/rittenregistratie/' },
      { name: 'GPS Tracker Vergelijken', href: '/gps-tracking/vergelijken/' },
      { name: 'Hardware & Producten', href: '/hardware/' },
    ],
    company: [
      { name: 'Over Ons', href: '/over-ons/' },
      { name: 'Blog', href: '/blog/' },
      { name: 'Prijzen & Hardware', href: '/prijzen/' },
      { name: 'Besparingscalculator', href: '/besparingscalculator/' },
      { name: 'Offerte Aanvragen', href: '/offerte/' },
      { name: 'Direct Aanmelden', href: '/aanmelden/' },
      { name: 'Vacatures', href: '/vacatures/' },
      { name: 'Contact', href: '/contact/' },
    ],
    kennisbank: [
      { name: 'Kennisbank (Woordenboek)', href: '/woordenboek/' },
      { name: 'GPS Rittenregistratie', href: '/blog/gps-ritregistratie/' },
      { name: 'Kilometerheffing 2026', href: '/blog/kilometerheffing-nederland-2026/' },
      { name: 'Brandstofkosten Verlagen', href: '/blog/brandstofkosten-verlagen-10-tips/' },
      { name: 'GPS Tracker Kosten 2026', href: '/blog/gps-tracker-kosten-2026/' },
      { name: 'Alle Artikelen', href: '/blog/' },
    ],
    legal: [
      { name: 'Algemene Voorwaarden', href: '/algemene-voorwaarden/' },
      { name: 'Privacybeleid', href: '/privacybeleid/' },
      { name: 'Cookiebeleid', href: '/cookie-beleid/' },
    ],
    trackers: [
      { name: 'GPS Tracker Bestelwagen', href: '/gps-tracker/bestelwagen/' },
      { name: 'GPS Tracker Vrachtwagen', href: '/gps-tracker/vrachtwagen/' },
      { name: 'GPS Tracker Bouwmaterieel', href: '/gps-tracker/bouwmaterieel/' },
      { name: 'GPS Tracker Camper', href: '/gps-tracker/camper/' },
      { name: 'Alle GPS Trackers', href: '/gps-tracker/' },
    ],
    locations: [
      { name: 'Amsterdam', href: '/amsterdam/' },
      { name: 'Rotterdam', href: '/rotterdam/' },
      { name: 'Utrecht', href: '/utrecht/' },
      { name: 'Den Haag', href: '/den-haag/' },
      { name: 'Eindhoven', href: '/eindhoven/' },
      { name: 'Groningen', href: '/groningen/' },
      { name: 'Tilburg', href: '/tilburg/' },
      { name: 'Almere', href: '/almere/' },
      { name: 'Breda', href: '/breda/' },
      { name: 'Nijmegen', href: '/nijmegen/' },
      { name: 'Haarlem', href: '/haarlem/' },
      { name: 'Arnhem', href: '/arnhem/' },
      { name: 'Zaanstad', href: '/zaanstad/' },
      { name: 'Amersfoort', href: '/amersfoort/' },
      { name: 'Apeldoorn', href: '/apeldoorn/' },
      { name: 'Den Bosch', href: '/den-bosch/' },
      { name: 'Enschede', href: '/enschede/' },
      { name: 'Leiden', href: '/leiden/' },
      { name: 'Maastricht', href: '/maastricht/' },
      { name: 'Zwolle', href: '/zwolle/' },
    ],
    sectoren: [
      { name: 'Transport & Logistiek', href: '/fleet-management/transport/' },
      { name: 'Koeriersdiensten', href: '/fleet-management/koeriers/' },
      { name: 'Servicebedrijven', href: '/fleet-management/service/' },
      { name: 'Thuiszorg', href: '/fleet-management/thuiszorg/' },
      { name: 'Landbouw & Agri', href: '/fleet-management/landbouw/' },
      { name: 'Gemeenten & Overheid', href: '/fleet-management/overheid/' },
      { name: 'Schoonmaakbedrijven', href: '/fleet-management/schoonmaak/' },
      { name: 'Hoveniers', href: '/fleet-management/hoveniers/' },
      { name: 'Verhuisbedrijven', href: '/fleet-management/verhuizers/' },
      { name: 'Afvalbeheer', href: '/fleet-management/afvalbeheer/' },
      { name: 'Catering & Horeca', href: '/fleet-management/catering/' },
      { name: 'Farmaceutisch', href: '/fleet-management/farmaceutisch/' },
      { name: 'Bouwsector', href: '/rittenregistratie/bouw/' },
      { name: 'Taxi & VTC', href: '/rittenregistratie/taxi/' },
      { name: 'Installateurs', href: '/rittenregistratie/installateurs/' },
      { name: 'Vastgoed & Makelaars', href: '/rittenregistratie/vastgoed/' },
    ],
  };


  const socialLinks = [
    {
      name: 'LinkedIn',
      href: 'https://www.linkedin.com/company/fleet-track-holland',
      icon: Linkedin,
    },
    {
      name: 'Facebook',
      href: 'https://www.facebook.com/profile.php?id=61587223371148&locale=nl_NL',
      icon: Facebook,
    },
    {
      name: 'Instagram',
      href: 'https://www.instagram.com/fleettrackholland',
      icon: Instagram,
    },
  ];

  return (
    <footer className="bg-slate-900 pt-20 pb-10 text-slate-300 overflow-hidden relative" aria-label="Site footer">
      {/* Decorative circle */}
      <div className="absolute top-0 right-0 -mr-20 -mt-20 opacity-5 pointer-events-none" aria-hidden="true">
        <svg width="400" height="400" viewBox="0 0 400 400" fill="none">
          <circle cx="200" cy="200" r="180" stroke="white" strokeWidth="40" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-8 gap-12 lg:gap-6">

          {/* Brand column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center mb-6" aria-label="FleetTrack Holland — Home">
              <img
                src="/footer-logo-white.png"
                alt="FleetTrack Holland"
                className="h-10 w-auto"
                width="160"
                height="40"
                loading="lazy"
              />
            </Link>
            <p className="text-slate-400 mb-4 max-w-sm leading-relaxed text-sm">
              Specialist in GPS fleet management en track & trace oplossingen voor ondernemers in Nederland. Verhoog uw efficiëntie en verlaag uw kosten vandaag nog.
            </p>

            {/* Business Info */}
            <div className="not-italic text-sm text-slate-500 mb-6 flex items-start">
              <Building2 className="h-4 w-4 mr-2 mt-0.5 text-blue-500 flex-shrink-0" aria-hidden="true" />
              <span>KvK: 88606902<br />BTW: NL004672676B48</span>
            </div>

            {/* Social media */}
            <div className="flex items-center space-x-3 mb-8">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-blue-600 flex items-center justify-center transition-colors border border-slate-700 hover:border-blue-600"
                  aria-label={`Volg ons op ${social.name}`}
                >
                  <social.icon className="h-4 w-4" aria-hidden="true" />
                </a>
              ))}
            </div>

            {/* Offerte CTA — verving de nep-nieuwsbrief (form had geen backend) */}
            <div className="bg-slate-800/50 p-5 rounded-2xl border border-slate-700/50 max-w-sm">
              <h3 className="text-white font-bold mb-2 text-sm flex items-center">
                <Mail className="h-4 w-4 mr-2 text-blue-500" aria-hidden="true" />
                Gratis offerte
              </h3>
              <p className="text-slate-400 text-xs mb-3">Vrijblijvend en op maat. Reactie binnen 24 uur op werkdagen.</p>
              <Link
                to="/offerte/"
                className="inline-flex items-center justify-center w-full bg-blue-600 hover:bg-blue-500 transition-colors px-4 py-2.5 rounded-xl text-white font-bold text-sm"
              >
                Offerte aanvragen
              </Link>
            </div>
          </div>

          {/* Oplossingen */}
          <div>
            <h3 className="text-white font-bold mb-5 text-xs tracking-wider uppercase">Oplossingen</h3>
            <ul className="space-y-3">
              {footerLinks.solutions.map((link) => (
                <li key={link.name}>
                  <Link
                    to={internalLink(link.href)}
                    className="hover:text-blue-400 transition-colors flex items-center group text-sm"
                  >
                    <ChevronRight className="h-3 w-3 mr-1 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all text-blue-500 flex-shrink-0" aria-hidden="true" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Bedrijf */}
          <div>
            <h3 className="text-white font-bold mb-5 text-xs tracking-wider uppercase">Bedrijf</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={internalLink(link.href)}
                    className="hover:text-blue-400 transition-colors flex items-center group text-sm"
                  >
                    <ChevronRight className="h-3 w-3 mr-1 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all text-blue-500 flex-shrink-0" aria-hidden="true" />
                    {link.name}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href="https://portal.fleettrackholland.nl"
                  className="hover:text-blue-400 transition-colors flex items-center group text-sm"
                >
                  <ChevronRight className="h-3 w-3 mr-1 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all text-blue-500 flex-shrink-0" aria-hidden="true" />
                  Klantenportaal
                </a>
              </li>
            </ul>
          </div>

          {/* Sectoren */}
          <div>
            <h3 className="text-white font-bold mb-5 text-xs tracking-wider uppercase">Sectoren</h3>
            <ul className="space-y-3">
              {footerLinks.sectoren.map((link) => (
                <li key={link.name}>
                  <Link
                    to={internalLink(link.href)}
                    className="hover:text-blue-400 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            <h3 className="text-white font-bold mb-4 mt-8 text-xs tracking-wider uppercase">Regio's</h3>
            <ul className="space-y-2">
              {footerLinks.locations.map((link) => (
                <li key={link.name}>
                  <Link
                    to={internalLink(link.href)}
                    className="hover:text-blue-400 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-bold mb-5 text-xs tracking-wider uppercase">Support & Contact</h3>
            <ul className="space-y-4 mb-6">
              <li className="flex items-center">
                <Mail className="h-4 w-4 text-blue-500 mr-3 flex-shrink-0" aria-hidden="true" />
                <EmailLink className="hover:text-blue-400 transition-colors text-sm" />
              </li>
            </ul>
            <div className="bg-slate-800/30 p-4 rounded-xl border border-slate-700/30 mb-6">
              <p className="text-xs text-slate-500 mb-1">Klantenservice open:</p>
              <p className="text-xs font-medium text-white">Ma – Vr: 08:30 – 17:30</p>
            </div>

            <p className="text-xs text-slate-400 mb-3">
              Bel: <a href="tel:+31627246429" className="font-medium text-white hover:underline">+31 6 27246429</a>
            </p>
          </div>
        </div>

        {/* GPS Trackers per voertuigtype */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12 pt-10 border-t border-slate-800">
          <div>
            <h3 className="text-white font-bold mb-5 text-xs tracking-wider uppercase">GPS Trackers</h3>
            <ul className="space-y-3">
              {footerLinks.trackers.map((link) => (
                <li key={link.name}>
                  <Link
                    to={internalLink(link.href)}
                    className="hover:text-blue-400 transition-colors flex items-center group text-sm"
                  >
                    <ChevronRight className="h-3 w-3 mr-1 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all text-blue-500 flex-shrink-0" aria-hidden="true" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-white font-bold mb-5 text-xs tracking-wider uppercase">Kennisbank</h3>
            <ul className="space-y-3">
              {footerLinks.kennisbank.map((link) => (
                <li key={link.name}>
                  <Link
                    to={internalLink(link.href)}
                    className="hover:text-blue-400 transition-colors flex items-center group text-sm"
                  >
                    <ChevronRight className="h-3 w-3 mr-1 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all text-blue-500 flex-shrink-0" aria-hidden="true" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <hr className="my-12 border-slate-800" />

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row justify-between items-center text-sm gap-4">
          <p className="text-slate-500 text-center md:text-left">
            &copy; {currentYear} <strong className="text-slate-300">FleetTrack Holland</strong> — Alle rechten voorbehouden.
          </p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            {footerLinks.legal.map((link) => (
              <Link
                key={link.name}
                to={internalLink(link.href)}
                className="text-slate-500 hover:text-white transition-colors text-xs whitespace-nowrap"
              >
                {link.name}
              </Link>
            ))}
            <a
              href="https://aanloopai.nl/werk/fth/"
              className="text-slate-500 hover:text-white transition-colors text-xs whitespace-nowrap"
            >
              Website door Aanloop AI
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
